// A company's own dispositions (D-188): what a telecaller picks after an answered call and after one nobody picked up,
// in the company's words. Each works as one of the outcomes the CRM acts on, so follow-ups, visits and reports carry on
// as before. A company with none keeps the standard choices. Settings edits them; the database checks them again
// (save_dispositions).

export type DispositionCall = "answered" | "unanswered";

/** The outcomes the CRM acts on, as record_outcome() knows them. */
export type ActsAs =
  | "call_back"
  | "interested"
  | "visit_booked"
  | "not_now"
  | "not_interested"
  | "wrong_number"
  | "not_connected";

export interface Disposition {
  /** Absent for one not saved yet. */
  id?: string;
  label: string;
  /** Its name in Hindi, for phones in Hindi; empty: the English one. */
  label_hi: string;
  acts_as: ActsAs;
  /** She writes what happened when she picks it, as for "Others" (D-192). */
  asks_note?: boolean;
}

export interface Dispositions {
  answered: Disposition[];
  unanswered: Disposition[];
}

/** What each outcome does, as Settings offers them for answered calls. */
export const ACTS_AS: { value: ActsAs; label: string; does: string }[] = [
  { value: "call_back", label: "Call back", does: "asks when, and reminds her then" },
  { value: "interested", label: "Interested", does: "asks when to follow up; the customer becomes Interested" },
  { value: "visit_booked", label: "Visit or appointment", does: "asks the day and time, and books it" },
  { value: "not_now", label: "Nothing for now", does: "saves the call; nothing changes" },
  { value: "not_interested", label: "Not interested", does: "the customer is lost, with this as the reason" },
  { value: "wrong_number", label: "Wrong number", does: "the number is not a lead" },
  { value: "not_connected", label: "Not connected", does: "counts as not answered; tried again by the rule" },
];

/** What a call nobody answered may do. */
export const UNANSWERED_ACTS: ActsAs[] = ["not_connected", "call_back", "not_interested", "wrong_number"];

/** The standard choices, offered while a company has none, as a list to start editing from. */
export const STANDARD: Dispositions = {
  answered: [
    { label: "Call back", label_hi: "", acts_as: "call_back" },
    { label: "Interested", label_hi: "", acts_as: "interested" },
    { label: "Visit booked", label_hi: "", acts_as: "visit_booked" },
    { label: "Not now", label_hi: "", acts_as: "not_now" },
    { label: "Not interested", label_hi: "", acts_as: "not_interested" },
    { label: "Wrong number", label_hi: "", acts_as: "wrong_number" },
  ],
  unanswered: [{ label: "Not answered", label_hi: "", acts_as: "not_connected" }],
};

/** The lists as the database gives them, keeping only what is readable. */
export function parseDispositions(rows: unknown): Disposition[] {
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((r) => {
    if (!r || typeof r !== "object") return [];
    const o = r as Record<string, unknown>;
    const acts = ACTS_AS.find((a) => a.value === o.acts_as)?.value;
    if (typeof o.label !== "string" || !acts) return [];
    return [{
      ...(typeof o.id === "string" ? { id: o.id } : {}),
      label: o.label,
      label_hi: typeof o.label_hi === "string" ? o.label_hi : "",
      acts_as: acts,
      ...(o.asks_note === true ? { asks_note: true } : {}),
    }];
  });
}

/** What is wrong with a list before it is saved, in the database's words, or null. */
export function dispositionsProblem(call: DispositionCall, list: Disposition[]): string | null {
  if (list.length > 20) return "Keep it to 20 choices.";
  const seen = new Set<string>();
  for (const d of list) {
    const label = d.label.trim();
    if (label.length < 1 || label.length > 40) return "Name each choice, in up to 40 characters.";
    if (d.label_hi.trim().length > 40) return `Keep the Hindi name of "${label}" to 40 characters.`;
    if (call === "unanswered" && !UNANSWERED_ACTS.includes(d.acts_as)) return `Choose what "${label}" does.`;
    const key = label.toLowerCase();
    if (seen.has(key)) return "Two choices have the same name.";
    seen.add(key);
  }
  return null;
}

/** A list as save_dispositions() takes it. */
export function dispositionsJson(
  list: Disposition[],
): { id?: string; label: string; label_hi: string | null; acts_as: ActsAs; asks_note?: true }[] {
  return list.map((d) => ({
    ...(d.id ? { id: d.id } : {}),
    label: d.label.trim(),
    label_hi: d.label_hi.trim() || null,
    acts_as: d.acts_as,
    ...(d.asks_note ? { asks_note: true as const } : {}),
  }));
}
